// src/utils/auditLogScope.js
//
// District tenancy: audit log visibility scoping (pure logic — no Firebase).
//
// firestore.rules reads auditLogs as `isSuperAdmin() && auditLogInScope()`:
// district tier reads everything; school-tier super admins only entries
// stamped with one of their schoolIds. Rules are NOT filters — a school-tier
// LIST query must carry a schoolId constraint or the whole query is rejected
// the moment any out-of-school stamped doc exists. This module computes that
// constraint plus the filter-UI model from the caller's role/claims.
//
// Convention: a log's schoolId is the school the actor's session was working
// in at the time. Logs written with no active school (district operations,
// legacy pre-stamping entries) have no schoolId and surface under the
// district reader's "District / system" filter.

/** Roles that may read audit logs at all — mirrors firestore.rules isSuperAdmin(). */
export const AUDIT_LOG_READ_ROLES = ['admin', 'district_admin', 'school_admin', 'admin_504']

/** Value of the school filter meaning "logs with no schoolId" (district readers only). */
export const DISTRICT_SYSTEM_FILTER = '__district__'

export function canReadAuditLogs(role) {
  return AUDIT_LOG_READ_ROLES.includes(role)
}

/**
 * District-tier readers may query unconstrained: district_admin by role, and
 * `admin` whose schoolIds claim is absent (vendor / district admin).
 * School-tier tokens (including school_admin with a missing claim) must
 * constrain — tight auditLogListInScope denies the old null-claim bypass.
 * An empty ARRAY claim is school-tier, same as the rules.
 */
export function isDistrictWideReader(role, schoolIds) {
  if (!canReadAuditLogs(role)) return false
  if (role === 'district_admin') return true
  return role === 'admin' && schoolIds == null
}

/**
 * Firestore schoolId constraint for audit log list queries.
 *
 * The LIST rule is strict (in query evaluation an unconstrained field reads
 * as null), so a school-tier token with a schoolIds claim must ALWAYS query
 * with a schoolId constraint — even in a single-school district. Unstamped
 * legacy entries can't match that constraint; they stay visible to district
 * tier ("District / system") and to pre-migration tokens with no claim, and
 * the schoolId backfill (SCHOOL_SCOPED_COLLECTIONS already includes
 * auditLogs) folds them into the school's scoped view.
 *
 * @param {object} args
 * @param {string} args.role
 * @param {string[]|null|undefined} args.schoolIds  the caller's schoolIds claim
 * @param {string} [args.selected]  active school filter ('' = all in scope,
 *   DISTRICT_SYSTEM_FILTER = no-schoolId logs — handled client-side)
 * @returns {null|{ type: 'eq'|'in', value: string|string[] }}
 *   null = no constraint needed/allowed; otherwise apply a where() clause.
 */
export function auditLogQueryScope({ role, schoolIds, selected = '' }) {
  if (!canReadAuditLogs(role)) return null

  if (isDistrictWideReader(role, schoolIds)) {
    if (selected && selected !== DISTRICT_SYSTEM_FILTER) return { type: 'eq', value: selected }
    return null // "All schools" — and "District / system" is client-filtered
  }

  const ids = Array.isArray(schoolIds) ? schoolIds : []

  // School-tier with a specific in-scope school picked.
  if (selected && ids.includes(selected)) return { type: 'eq', value: selected }

  // Degenerate empty-array claim: no school is readable, so match nothing
  // rather than run unconstrained (which the rules would reject).
  if (ids.length === 0) return { type: 'eq', value: '__no_school__' }

  if (ids.length === 1) return { type: 'eq', value: ids[0] }
  return { type: 'in', value: ids.slice(0, 30) } // Firestore 'in' cap
}

/**
 * School filter dropdown model.
 *
 * @returns {Array<{ value: string, label: string }>}
 *   District-wide readers: All schools / each school / District & system.
 *   School-tier with 2+ schools in a multi-school district: All my schools /
 *   each of their schools. Everyone else: no picker (empty array).
 */
export function auditLogFilterOptions({
  role,
  schoolIds,
  schools = [],
  isMultiSchoolDistrict = false
}) {
  if (!canReadAuditLogs(role)) return []
  const nameOf = (id) => schools.find((s) => s.id === id)?.name || id

  if (isDistrictWideReader(role, schoolIds)) {
    return [
      { value: '', label: 'All schools' },
      ...schools.map((s) => ({ value: s.id, label: s.name || s.id })),
      { value: DISTRICT_SYSTEM_FILTER, label: 'District / system (no school)' }
    ]
  }

  const ids = Array.isArray(schoolIds) ? schoolIds : []
  if (!isMultiSchoolDistrict || ids.length < 2) return []
  return [
    { value: '', label: 'All my schools' },
    ...ids.map((id) => ({ value: id, label: nameOf(id) }))
  ]
}
